import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, EffectFade, Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/effect-fade';
import 'swiper/css/pagination';
import { FaPlay, FaArrowRight } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import VideoModal from './VideoModal';

const HeroSection = ({ onGetStarted }) => {
  const { t } = useTranslation();
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const heroImages = [
    'https://images.unsplash.com/photo-1608377205619-03a0b4c4e270?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwzfHxob21lJTIwYXV0b21hdGlvbnxlbnwwfHx8fDE3NzI2MzM3OTd8MA&ixlib=rb-4.1.0&q=85',
    'https://images.unsplash.com/photo-1513584684374-8bab748fbf90?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwyfHxzbWFydCUyMGhvbWV8ZW58MHx8fHwxNzcyNjMzNzkzfDA&ixlib=rb-4.1.0&q=85',
    'https://images.unsplash.com/photo-1628744876497-eb30460be9f6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBpbnRlcmlvcnxlbnwwfHx8fDE3NzI2MzM4MDV8MA&ixlib=rb-4.1.0&q=85',
  ];

  return (
    <section id="home" className="relative h-screen w-full overflow-hidden" data-testid="hero-section">
      {/* Background Slider */}
      <div className="absolute inset-0 z-0">
        <Swiper
          modules={[Autoplay, EffectFade, Pagination]}
          effect="fade"
          autoplay={{
            delay: 5000,
            disableOnInteraction: false,
          }}
          pagination={{
            clickable: true,
            bulletClass: 'swiper-pagination-bullet !bg-[#00d4ff]',
          }}
          loop={true}
          className="h-full w-full"
        >
          {heroImages.map((image, index) => (
            <SwiperSlide key={index}>
              <div
                className="h-full w-full bg-cover bg-center"
                style={{
                  backgroundImage: `linear-gradient(rgba(10, 14, 39, 0.65), rgba(10, 14, 39, 0.7)), url(${image})`,
                }}
              />
            </SwiperSlide>
          ))}
        </Swiper>
      </div>

      {/* Animated Background Elements */}
      <div className="absolute inset-0 z-10 pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-[#00d4ff]/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#0099ff]/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
      </div>

      {/* Content - Mobile Optimized */}
      <div className="relative z-20 h-full flex items-center">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
            {/* Left: Text Content - Mobile Friendly */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center md:text-left"
            >
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="inline-block glass px-4 py-2 md:px-6 md:py-2 rounded-full mb-4 md:mb-6"
              >
                <span className="text-[#00d4ff] font-semibold uppercase tracking-wider text-xs md:text-sm">
                  {t('hero.badge')}
                </span>
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-4 md:mb-6 leading-tight"
              >
                {t('hero.title')}{' '}
                <span className="gradient-text block mt-2">
                  {t('hero.subtitle')}
                </span>
              </motion.h1>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8 }}
                className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center md:justify-start"
              >
                <button onClick={onGetStarted} className="btn-primary group px-6 py-3 md:px-8 md:py-4" data-testid="get-started-btn">
                  <span>{t('hero.getStarted')}</span>
                  <FaArrowRight className="inline-block ml-2 group-hover:translate-x-2 transition-transform" />
                </button>
                <button 
                  onClick={() => setIsVideoModalOpen(true)}
                  className="btn-secondary group px-6 py-3 md:px-8 md:py-4"
                >
                  <FaPlay className="inline-block mr-2" />
                  <span>{t('hero.watchDemo')}</span>
                </button>
              </motion.div>

              {/* Features - Mobile Optimized */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1 }}
                className="grid grid-cols-3 gap-3 md:gap-6 mt-8 md:mt-12"
              >
                {[
                  { icon: '🔒', label: t('hero.feature1') },
                  { icon: '⚡', label: t('hero.feature2') },
                  { icon: '📱', label: t('hero.feature3') },
                ].map((feature, index) => (
                  <div key={index} className="text-center">
                    <div className="text-2xl md:text-3xl mb-1 md:mb-2">{feature.icon}</div>
                    <p className="text-white/70 text-xs md:text-sm font-medium">{feature.label}</p>
                  </div>
                ))}
              </motion.div>
            </motion.div>

            {/* Right: Smart Home Image with Advanced Animations */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="hidden md:flex justify-center items-center"
            >
              <div className="relative w-full max-w-lg">
                {/* Animated rotating gradient border */}
                <div className="absolute inset-0 rounded-3xl overflow-hidden">
                  <div className="absolute inset-0 rounded-3xl animate-spin-slow bg-gradient-to-r from-[#00d4ff] via-[#0099ff] to-[#00d4ff] opacity-50 blur-xl" 
                       style={{ 
                         animation: 'spin 8s linear infinite',
                         transformOrigin: 'center'
                       }} />
                </div>

                {/* Pulsing glow layers */}
                <motion.div
                  animate={{
                    scale: [1, 1.05, 1],
                    opacity: [0.3, 0.6, 0.3]
                  }}
                  transition={{
                    duration: 3,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                  className="absolute inset-0 bg-gradient-to-br from-[#00d4ff]/30 to-[#0099ff]/30 rounded-3xl blur-2xl"
                />

                {/* Floating particles */}
                {[...Array(5)].map((_, i) => (
                  <motion.div
                    key={i}
                    className="absolute w-2 h-2 bg-[#00d4ff] rounded-full"
                    style={{
                      top: `${20 + i * 15}%`,
                      left: `${-10 + i * 5}%`,
                    }}
                    animate={{
                      y: [0, -30, 0],
                      x: [0, 15, 0],
                      opacity: [0.2, 0.8, 0.2]
                    }}
                    transition={{
                      duration: 3 + i * 0.5,
                      repeat: Infinity,
                      ease: "easeInOut",
                      delay: i * 0.4
                    }}
                  />
                ))}

                {/* Main image container with glass effect */}
                <motion.div 
                  className="relative glass-strong rounded-3xl p-6 overflow-hidden"
                  whileHover={{ scale: 1.02 }}
                  transition={{ duration: 0.3 }}
                  style={{
                    boxShadow: '0 0 40px rgba(0, 212, 255, 0.3), 0 0 80px rgba(0, 153, 255, 0.2)'
                  }}
                >
                  {/* Scanning line effect */}
                  <motion.div
                    className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#00d4ff] to-transparent"
                    animate={{
                      y: [0, 400, 0]
                    }}
                    transition={{
                      duration: 4,
                      repeat: Infinity,
                      ease: "linear"
                    }}
                  />

                  {/* Corner accents */}
                  <div className="absolute top-4 left-4 w-8 h-8 border-t-2 border-l-2 border-[#00d4ff]" />
                  <div className="absolute top-4 right-4 w-8 h-8 border-t-2 border-r-2 border-[#00d4ff]" />
                  <div className="absolute bottom-4 left-4 w-8 h-8 border-b-2 border-l-2 border-[#00d4ff]" />
                  <div className="absolute bottom-4 right-4 w-8 h-8 border-b-2 border-r-2 border-[#00d4ff]" />

                  {/* Main Image */}
                  <img
                    src="https://customer-assets.emergentagent.com/job_smart-future-4/artifacts/33mc3d6h_SSH%20website%20mockup%202.png"
                    alt="Sheba Smart Home - Complete Professional Solution"
                    className="w-full h-auto rounded-2xl relative z-10"
                  />

                  {/* Holographic overlay */}
                  <div className="absolute inset-0 bg-gradient-to-tr from-[#00d4ff]/0 via-[#00d4ff]/10 to-[#00d4ff]/0 pointer-events-none" />
                </motion.div>

                {/* Orbiting dots */}
                {[0, 120, 240].map((rotation, i) => (
                  <motion.div
                    key={i}
                    className="absolute w-3 h-3 bg-[#00d4ff] rounded-full shadow-lg shadow-[#00d4ff]/50"
                    style={{
                      top: '50%',
                      left: '50%',
                      marginTop: '-6px',
                      marginLeft: '-6px'
                    }}
                    animate={{
                      rotate: [rotation, rotation + 360]
                    }}
                    transition={{
                      duration: 8,
                      repeat: Infinity,
                      ease: "linear"
                    }}
                  >
                    <div style={{
                      transform: 'translateX(200px)',
                      position: 'relative'
                    }} />
                  </motion.div>
                ))}

                {/* Background glow effects */}
                <div className="absolute -z-10 -top-10 -right-10 w-40 h-40 bg-[#00d4ff]/20 rounded-full blur-3xl animate-pulse" />
                <div className="absolute -z-10 -bottom-10 -left-10 w-40 h-40 bg-[#0099ff]/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        className="absolute bottom-6 md:bottom-8 left-1/2 -translate-x-1/2 z-20"
      >
        <div className="flex flex-col items-center">
          <span className="text-white/60 text-xs md:text-sm mb-2">{t('hero.scrollToExplore')}</span>
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ repeat: Infinity, duration: 1.5 }}
            className="w-5 h-8 md:w-6 md:h-10 border-2 border-white/40 rounded-full flex items-start justify-center p-1 md:p-2"
          >
            <div className="w-1 h-2 md:h-3 bg-[#00d4ff] rounded-full" />
          </motion.div>
        </div>
      </motion.div>

      {/* Video Modal */}
      <VideoModal
        isOpen={isVideoModalOpen}
        onClose={() => setIsVideoModalOpen(false)}
        videoUrl="https://youtu.be/9A3SdlwwUV4"
      />
    </section>
  );
};

export default HeroSection;
